// エントリー詳細の「メッセージ」（第1.5弾で DM パネルに昇格・旧SPA app.js の DM パネル移植）。
// Instagram 連携済み（sns.instagram に ig_follower_id あり）= 履歴末尾30件＋テンプレ挿入＋アプリから送信。
// 未連携 = テンプレ差し込み文面をコピー（従来の MessageModal と同じ）。
// 送信は相手に本当に届いて取り消せない → 確認ダイアログ必須・1000字上限（gifting /api/ig/friends/:fid/messages と同じ）。
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchGfIgMessages, fetchGfTemplates, postGfContacted, sendGfIgMessage } from "../../../../api/giftingClient";
import { DM_MAX_CHARS, fillTpl, fmtDateTime, linkedIgOf, type TplContext } from "../../../../lib/giftingRules";
import type { GfIgMessage, GfSns, GfTemplate, Loadable } from "../../../../types/gifting";
import { errMsg } from "../../gfUtil";
import { ConfirmModal } from "../../modals/ConfirmModal";
import { GfButton, GfField, GfModal, GfSkeleton, copyText } from "../../ui/GfUi";

const HISTORY_LIMIT = 30;

export function DmPanel({
  open,
  ctx,
  sns,
  dealId,
  influencerId,
  /** 段階が閉じた（アーカイブ/中止）エントリーでは送信後の「連絡を記録」をしない */
  closed,
  onClose,
  onSent,
  onOpenCreator,
  toast,
}: {
  open: boolean;
  ctx: TplContext;
  sns: GfSns[];
  dealId: string;
  influencerId: string;
  closed: boolean;
  onClose: () => void;
  onSent: () => Promise<void>;
  onOpenCreator: () => void;
  toast: (m: string) => void;
}) {
  const ig = linkedIgOf(sns);
  const fid = ig?.ig_follower_id ?? null;

  const [templates, setTemplates] = useState<Loadable<GfTemplate[]>>({ status: "loading" });
  const [tplId, setTplId] = useState("");
  const [text, setText] = useState("");
  const [history, setHistory] = useState<Loadable<GfIgMessage[]> | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  // テンプレ一覧（開くたび）。未連携ではすぐコピーできるよう先頭を選んでおく
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setTemplates({ status: "loading" });
    setText("");
    setTplId("");
    fetchGfTemplates().then(
      (list) => {
        if (!alive) return;
        const sorted = [...list].sort((a, b) => a.sort_order - b.sort_order);
        setTemplates({ status: "ok", data: sorted });
        if (fid === null && sorted[0]) {
          setTplId(sorted[0].id);
          setText(fillTpl(sorted[0].body, ctx));
        }
      },
      (e: unknown) => alive && setTemplates({ status: "error", message: errMsg(e) })
    );
    return () => {
      alive = false;
    };
    // ctx は開いた時点の値でよい（開いている間に変わるのは保存直後だけ）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fid]);

  const loadHistory = useCallback(async () => {
    if (fid === null) return;
    setHistory((h) => (h?.status === "ok" ? h : { status: "loading" }));
    try {
      const rows = await fetchGfIgMessages(fid);
      setHistory({ status: "ok", data: rows.slice(-HISTORY_LIMIT) });
    } catch (e) {
      setHistory({ status: "error", message: errMsg(e) });
    }
  }, [fid]);

  useEffect(() => {
    if (!open || fid === null) return;
    setHistory({ status: "loading" });
    void loadHistory();
  }, [open, fid, loadHistory]);

  // 履歴は最新が下。読み込んだら一番下へ
  useEffect(() => {
    if (history?.status === "ok" && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [history]);

  const list = templates.status === "ok" ? templates.data : [];
  const pickTemplate = (id: string) => {
    setTplId(id);
    const t = list.find((x) => x.id === id);
    if (t) setText(fillTpl(t.body, ctx));
  };

  const trimmed = text.trim();
  const over = trimmed.length > DM_MAX_CHARS;

  const send = async () => {
    if (fid === null || !trimmed || over) return;
    setSending(true);
    try {
      await sendGfIgMessage(fid, { text: trimmed, influencer_id: influencerId, deal_id: dealId });
    } catch (e) {
      // ig-harness の 24時間窓などはエラー文をそのまま見せる
      toast(`送信できませんでした: ${errMsg(e)}`);
      setSending(false);
      setConfirm(false);
      return;
    }
    let contactNote = "";
    if (!closed) {
      try {
        await postGfContacted(dealId, { channel: "ig_dm" });
      } catch (e) {
        contactNote = `（連絡日の記録に失敗: ${errMsg(e)}）`;
      }
    }
    toast(`DMを送信しました${contactNote}`);
    setText("");
    setTplId("");
    setConfirm(false);
    setSending(false);
    await Promise.all([loadHistory(), onSent()]);
  };

  const copy = () => void copyText(trimmed).then((ok) => toast(ok ? "メッセージをコピーしました" : "コピーできませんでした"));

  return (
    <>
      <GfModal
        open={open}
        title={fid !== null ? `Instagram DM（@${ig?.handle ?? ""}）` : `${ctx.name} さんへのメッセージ`}
        width={560}
        onClose={sending || confirm ? () => undefined : onClose}
        footer={
          <>
            <GfButton onClick={onClose} disabled={sending}>
              閉じる
            </GfButton>
            <GfButton disabled={!trimmed} onClick={copy}>
              コピー
            </GfButton>
            {fid !== null && (
              <GfButton variant="primary" disabled={!trimmed || over || sending} onClick={() => setConfirm(true)}>
                DMを送信
              </GfButton>
            )}
          </>
        }
      >
        {fid !== null ? (
          <div className="gf-dm">
            <div className="gf-dm-head">
              <span className="gf-note">直近{HISTORY_LIMIT}件</span>
              <button type="button" className="gf-link-btn" onClick={() => void loadHistory()} disabled={history?.status === "loading"}>
                履歴を更新
              </button>
            </div>
            <div className="gf-dm-list" ref={listRef} aria-label="DMの履歴">
              {(!history || history.status === "loading") && <GfSkeleton height={96} />}
              {history?.status === "error" && <div className="gf-banner is-error">DMの履歴を読み込めませんでした。{history.message}</div>}
              {history?.status === "ok" && history.data.length === 0 && <div className="gf-note">履歴がありません</div>}
              {history?.status === "ok" &&
                history.data.map((m) => (
                  <div key={m.id} className={`gf-dm-msg ${m.direction === "out" ? "is-out" : "is-in"}`}>
                    <div className="gf-dm-bubble">{m.text}</div>
                    <time className="gf-dm-time">{fmtDateTime(m.created_at)}</time>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div className="gf-banner is-info">
            Instagram 未連携のため、ここからは送信できません。文面をコピーして Instagram などで送ってください。
            <button type="button" className="gf-link-btn" onClick={onOpenCreator}>
              クリエイター詳細でInstagram連携する
            </button>
          </div>
        )}

        {templates.status === "loading" && <GfSkeleton height={36} />}
        {templates.status === "error" && <div className="gf-banner is-error">テンプレートを読み込めませんでした。{templates.message}</div>}
        {templates.status === "ok" && list.length > 0 && (
          <GfField label="テンプレートを挿入">
            <select className="gf-select" value={tplId} onChange={(e) => pickTemplate(e.target.value)}>
              <option value="">— 選ぶと本文に差し込みます —</option>
              {list.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </GfField>
        )}
        <GfField
          label="本文"
          hint={
            <span className={over ? "gf-dm-count is-over" : "gf-dm-count"}>
              {trimmed.length} / {DM_MAX_CHARS}字{over ? "（1000字を超えています）" : ""}
            </span>
          }
        >
          <textarea
            className="gf-textarea"
            rows={7}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={fid !== null ? "メッセージを入力（送信は即時・取り消せません）" : "テンプレートを選ぶか、本文を入力"}
          />
        </GfField>
      </GfModal>

      <ConfirmModal
        open={confirm}
        title="このDMを送信しますか"
        message={`@${ig?.handle ?? ""} に Instagram DM を送信します。送信は取り消せません。`}
        confirmLabel={sending ? "送信中…" : "送信する"}
        busy={sending}
        onConfirm={() => void send()}
        onClose={() => !sending && setConfirm(false)}
      >
        <div className="gf-boxed gf-dm-preview">{trimmed.length > 300 ? `${trimmed.slice(0, 300)}…` : trimmed}</div>
      </ConfirmModal>
    </>
  );
}
